import type { LocalizedDict } from '../index'
import type {
  Capability,
  CapabilityCategory,
  RoleTier,
} from '../../lib/user-permissions'

// My Permissions page. Capability label/description come from the capability
// catalog (English); Turkish overrides them by capability id and falls back to
// the catalog text for any id not listed here.

type CapabilityText = { label: string; description: string }

export type MyPermissionsStrings = {
  loading: string
  loadError: string
  title: string
  subtitle: string
  granted: string
  grantedAt: string
  categories: Record<CapabilityCategory, string>
  tiers: Record<RoleTier, string>
  tierDescriptions: Record<RoleTier, string>
  capability: (c: Capability) => CapabilityText
}

const CAPABILITIES_TR: Record<string, CapabilityText> = {
  'catalog.view': { label: 'Kataloğu gör', description: 'Master katalog ürünlerini gör' },
  'catalog.edit': { label: 'Kataloğu düzenle', description: 'Ürün ekle / güncelle / arşivle' },
  'catalog.activate': { label: 'Ürünleri yayına al', description: 'Taslak → yayında geçişi' },
  'orders.view': { label: 'Siparişleri gör', description: 'Kendi organizasyonuna ait siparişleri gör' },
  'orders.update': { label: 'Siparişleri güncelle', description: 'Durum geçişi + etiket + atama' },
  'orders.bulk': { label: 'Toplu işlemler', description: 'Birden çok siparişte işlem (etiket / iptal)' },
  'orders.label_purchase': { label: 'Kargo etiketi satın al', description: 'B-4 §G kargo etiketi yeniden satışı' },
  'pricing.view': { label: 'Fiyatları gör', description: 'Kademe fiyatları + çarpan' },
  'pricing.edit': { label: 'Fiyatları düzenle', description: 'Ürün bazında çarpanı değiştir' },
  'pricing.hub_admin': { label: 'Fiyatlandırma merkezi yöneticisi', description: 'Genel çarpanlar + maliyet aralığı kuralları' },
  'drive.view': { label: 'Drive’ı gör', description: 'Organizasyonunla paylaşılan dosyaları gör' },
  'drive.upload': { label: 'Drive’a yükle', description: 'Dosya yükle + rol seç' },
  'drive.share': { label: 'Drive paylaş', description: 'Organizasyonlar arası erişim izni (D-5 Halka 2)' },
  'drive.audit_admin': { label: 'Drive denetim yöneticisi', description: 'Ürünler arası Halka 3 görünümü' },
  'wo.view': { label: 'İş emirlerini gör', description: 'Atanan iş emirlerini gör' },
  'wo.transition': { label: 'İş emri durum geçişi', description: 'assigned → shipped akışı' },
  'wo.upload_photo': { label: 'Üretim fotoğrafı yükle', description: 'İş emrine bağlı Drive dosyası yükleme' },
  'wo.packaging.advance': { label: 'Paketleme aşamasını ilerlet', description: 'NJ depo iş emri paketleme durum geçişi (T1.10 warehouse_operator varsayılanı)' },
  'team.view': { label: 'Ekibi gör', description: 'Üye listesi + roller' },
  'team.edit': { label: 'Ekibi düzenle', description: 'Davet et / çıkar / rol değiştir' },
  'settings.view': { label: 'Ayarları gör', description: 'Organizasyon ayarları' },
  'settings.edit': { label: 'Ayarları düzenle', description: 'Profil, bildirimler, marka' },
  'finance.view_costs': { label: 'Maliyetleri gör', description: 'Maliyet görünürlüğü (maliyet gizlemeyi atlar)' },
  'finance.wallet_admin': { label: 'Cüzdan yöneticisi', description: 'Mutabakat çalıştırmaları + ödemeler' },
  'platform.user_admin': { label: 'Kullanıcı yöneticisi', description: 'A1 Halka 3 yönetişimi' },
  'platform.audit_view': { label: 'Denetim kaydını gör', description: 'Organizasyonlar arası hareket akışı' },
  'platform.access_seller_orgs': { label: 'Satıcı organizasyonlarına erişim', description: 'D-5 Halka 3 — platform yetkisiyle herhangi bir satıcı paneline gir (salt okunur)' },
  'platform.access_vendor_orgs': { label: 'Tedarikçi organizasyonlarına erişim', description: 'D-5 Halka 3 — platform yetkisiyle herhangi bir tedarikçi paneline gir (salt okunur)' },
  'inventory.physical.adjust': { label: 'Fiziksel envanteri düzelt', description: 'NJ depo kabul/sevk işlemleri (T1.10 warehouse_operator varsayılanı)' },
  'ship.rate_quote.read': { label: 'Kargo fiyat teklifini gör', description: 'Kargo firması fiyat teklifi (admin hepsi + satıcı kendi + üretim kendi iş emri)' },
  'ship.label.purchase': { label: 'Kargo etiketi satın al', description: 'Kargo API’sinden etiket alma (admin hepsi + satıcı onaydan sonra)' },
  'ship.label.void': { label: 'Kargo etiketini iptal et', description: 'D-5 Halka 3 — yalnız admin (satıcı yalnız talep eder)' },
  'ship.tracking.read': { label: 'Takibi gör', description: 'Gönderi takibi (admin hepsi + satıcı kendi + üretim kendi iş emri + Etsy aktarımı)' },
  'ship.shipment.scan': { label: 'Gönderi barkodu okut', description: 'NJ depo okutma/sevk (yalnız admin + warehouse_operator)' },
}

export const myPermissionsI18n: LocalizedDict<MyPermissionsStrings> = {
  en: {
    loading: 'Loading…',
    loadError: "Couldn't load your permissions.",
    title: 'My Permissions',
    subtitle: 'The capabilities your account is granted by its role.',
    granted: 'granted',
    grantedAt: 'Granted at: ',
    categories: {
      catalog: 'Catalog',
      orders: 'Orders',
      pricing: 'Pricing',
      drive: 'Drive',
      production: 'Production',
      team: 'Team',
      settings: 'Settings',
      finance: 'Finance',
      platform: 'Platform (A1)',
      shipping: 'Shipping',
    },
    tiers: {
      owner: 'Owner',
      admin: 'Admin',
      designer: 'Designer',
      member: 'Member',
      customer_service: 'Customer Service',
      warehouse_operator: 'Warehouse Operator',
    },
    tierDescriptions: {
      owner: 'Full authority — all org functions (V1 default — every character is the owner of their own org)',
      admin: 'Operations manager — all functions except finance (V1.5+ tier)',
      designer: 'Design + listing focus — Design Studio + drive upload (V1.5+ tier)',
      member: 'Limited view + edit — assigned tasks only (V1.5+ tier)',
      customer_service: 'Customer communication + returns — order view + comm only (V1.5+ tier)',
      warehouse_operator: 'NJ depot operator — scan/dispatch + packaging + tracking (T1.10 A-32 §A7)',
    },
    capability: (c) => ({ label: c.label, description: c.description }),
  },
  tr: {
    loading: 'Yükleniyor…',
    loadError: 'Yetkilerin yüklenemedi.',
    title: 'Yetkilerim',
    subtitle: 'Hesabına rolü gereği verilen yetkiler.',
    granted: 'verildi',
    grantedAt: 'Verilme zamanı: ',
    categories: {
      catalog: 'Katalog',
      orders: 'Siparişler',
      pricing: 'Fiyatlandırma',
      drive: 'Drive',
      production: 'Üretim',
      team: 'Ekip',
      settings: 'Ayarlar',
      finance: 'Finans',
      platform: 'Platform (A1)',
      shipping: 'Kargo',
    },
    tiers: {
      owner: 'Sahip',
      admin: 'Yönetici',
      designer: 'Tasarımcı',
      member: 'Üye',
      customer_service: 'Müşteri hizmetleri',
      warehouse_operator: 'Depo operatörü',
    },
    tierDescriptions: {
      owner: 'Tam yetki — organizasyonun tüm işlevleri (V1 varsayılanı — her karakter kendi organizasyonunun sahibidir)',
      admin: 'Operasyon yöneticisi — finans dışında tüm işlevler (V1.5+ kademesi)',
      designer: 'Tasarım + ilan odaklı — Design Studio + Drive yükleme (V1.5+ kademesi)',
      member: 'Sınırlı görme + düzenleme — yalnız atanan işler (V1.5+ kademesi)',
      customer_service: 'Müşteri iletişimi + iadeler — yalnız sipariş görme + iletişim (V1.5+ kademesi)',
      warehouse_operator: 'NJ depo operatörü — okutma/sevk + paketleme + takip (T1.10 A-32 §A7)',
    },
    capability: (c) => CAPABILITIES_TR[c.id] ?? { label: c.label, description: c.description },
  },
}
